import { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import Badge from '../ui/Badge';
import { warrantyService } from '../../lib/warrantyService';
import { SoldProduct, SoldProductSupply } from '../../types';
import { formatDate } from '../../lib/dateUtils';

interface WarrantyClaimFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function WarrantyClaimForm({ onClose, onSuccess }: WarrantyClaimFormProps) {
  const [step, setStep] = useState<'search' | 'selectProduct' | 'selectSupply' | 'claimDetails'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SoldProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SoldProduct | null>(null);
  const [selectedSupply, setSelectedSupply] = useState<SoldProductSupply | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [claimData, setClaimData] = useState({
    claim_type: 'repair' as 'repair' | 'replacement' | 'refund' | 'denied',
    issue_description: '',
    handled_by: '',
    cost: 0,
  });

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      alert('Please enter a serial number or customer name');
      return;
    }

    try {
      setLoading(true);

      // Try serial number search first
      const serialResults = await warrantyService.getSoldProducts({
        serialNumber: searchTerm,
      });

      // If exact serial match found, go directly to supply selection
      if (serialResults.length === 1 && serialResults[0].serial_number.toLowerCase() === searchTerm.toLowerCase()) {
        setSelectedProduct(serialResults[0]);
        setStep('selectSupply');
        return;
      }

      // Otherwise, search by customer name too
      const customerResults = await warrantyService.getSoldProducts({
        customerName: searchTerm,
      });

      // Merge and deduplicate results
      const allResults = [...serialResults, ...customerResults];
      const uniqueResults = Array.from(
        new Map(allResults.map(item => [item.id, item])).values()
      );

      if (uniqueResults.length === 0) {
        alert('No products found matching your search');
        return;
      }

      if (uniqueResults.length === 1) {
        setSelectedProduct(uniqueResults[0]);
        setStep('selectSupply');
      } else {
        setSearchResults(uniqueResults);
        setStep('selectProduct');
      }
    } catch (error) {
      console.error('Error searching products:', error);
      alert('Failed to search products');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = async (product: SoldProduct) => {
    setSelectedProduct(product);
    setStep('selectSupply');
  };

  const handleSupplySelect = (supply: SoldProductSupply | null) => {
    setSelectedSupply(supply);
    setStep('claimDetails');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      alert('No product selected');
      return;
    }

    if (!claimData.issue_description.trim()) {
      alert('Please provide an issue description');
      return;
    }

    try {
      setSubmitting(true);
      await warrantyService.createWarrantyClaim({
        sold_product_id: selectedProduct.id,
        supply_id: selectedSupply?.supply_id,
        supply_name: selectedSupply?.supply_name,
        claim_type: claimData.claim_type,
        issue_description: claimData.issue_description,
        handled_by: claimData.handled_by || undefined,
        cost: claimData.cost,
        status: 'pending',
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating warranty claim:', error);
      alert('Failed to create warranty claim');
    } finally {
      setSubmitting(false);
    }
  };

  const getWarrantyStatus = (product: SoldProduct) => {
    const now = new Date();
    const warrantyEnd = new Date(product.warranty_end_date);
    return warrantyEnd >= now ? 'active' : 'expired';
  };

  const resetForm = () => {
    setStep('search');
    setSearchTerm('');
    setSearchResults([]);
    setSelectedProduct(null);
    setSelectedSupply(null);
    setClaimData({
      claim_type: 'repair',
      issue_description: '',
      handled_by: '',
      cost: 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Create Warranty Claim</h2>
            {selectedProduct && (
              <p className="text-sm text-slate-600 mt-1">
                Product: {selectedProduct.manufactured_product?.name || selectedProduct.supply?.name}
                <span className="font-mono ml-2">({selectedProduct.serial_number})</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'search' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Step 1: Find Product
                </h3>
                <p className="text-slate-600 mb-4">
                  Search by serial number or customer name to find the product with warranty coverage.
                </p>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      label="Serial Number or Customer Name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Enter serial number or customer name..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSearch} disabled={loading}>
                      <Search className="w-4 h-4 mr-2" />
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'selectProduct' && searchResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  Step 2: Select Product ({searchResults.length} found)
                </h3>
                <Button variant="secondary" size="sm" onClick={resetForm}>
                  New Search
                </Button>
              </div>

              <div className="space-y-3">
                {searchResults.map((product) => (
                  <Card
                    key={product.id}
                    className="p-4 hover:border-blue-500 cursor-pointer transition-colors"
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-slate-900">
                            {product.manufactured_product?.name || product.supply?.name}
                          </h4>
                          <Badge variant="secondary">{product.product_type}</Badge>
                          <Badge variant={getWarrantyStatus(product) === 'active' ? 'success' : 'secondary'}>
                            {getWarrantyStatus(product) === 'active' ? 'Active Warranty' : 'Expired Warranty'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <p className="text-slate-600">
                            <span className="font-medium">Serial:</span>
                            <span className="font-mono ml-1">{product.serial_number}</span>
                          </p>
                          <p className="text-slate-600">
                            <span className="font-medium">Customer:</span> {product.customer_name}
                          </p>
                          <p className="text-slate-600">
                            <span className="font-medium">Sale Date:</span> {formatDate(product.sale_date)}
                          </p>
                          <p className="text-slate-600">
                            <span className="font-medium">Warranty Until:</span> {formatDate(product.warranty_end_date)}
                          </p>
                        </div>
                      </div>
                      <div className="text-blue-600 text-sm font-medium whitespace-nowrap ml-4">
                        Click to select →
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {step === 'selectSupply' && selectedProduct && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  Step 3: Select Component (Optional)
                </h3>
                <Button variant="secondary" size="sm" onClick={resetForm}>
                  New Search
                </Button>
              </div>

              <p className="text-slate-600">
                Choose which specific component needs warranty, or select "Entire Product" if the issue affects the whole unit.
              </p>

              <div className="space-y-3">
                <Card
                  className="p-4 hover:border-blue-500 cursor-pointer transition-colors border-2"
                  onClick={() => handleSupplySelect(null)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Entire Product</p>
                      <p className="text-sm text-slate-600">General warranty claim not specific to one component</p>
                    </div>
                    <div className="text-blue-600 text-sm font-medium">Click to select →</div>
                  </div>
                </Card>

                {selectedProduct.supplies_used && selectedProduct.supplies_used.length > 0 ? (
                  <>
                    <div className="border-t border-slate-200 pt-3">
                      <p className="text-sm font-medium text-slate-700 mb-2">Or select a specific component:</p>
                    </div>
                    {selectedProduct.supplies_used.map((supply) => (
                      <Card
                        key={supply.id}
                        className="p-4 hover:border-blue-500 cursor-pointer transition-colors"
                        onClick={() => handleSupplySelect(supply)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{supply.supply_name}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                              {supply.lot_number && (
                                <span>
                                  <span className="font-medium">Lot:</span>
                                  <span className="font-mono ml-1">{supply.lot_number}</span>
                                </span>
                              )}
                              <span>
                                <span className="font-medium">Qty:</span> {supply.quantity_used} {supply.unit}
                              </span>
                            </div>
                          </div>
                          <div className="text-blue-600 text-sm font-medium">Click to select →</div>
                        </div>
                      </Card>
                    ))}
                  </>
                ) : (
                  <p className="text-sm text-slate-500 italic">
                    No component traceability data available for this product
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 'claimDetails' && selectedProduct && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  Step 4: Claim Details
                </h3>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setStep('selectSupply')}
                >
                  Back
                </Button>
              </div>

              <Card className="p-4 bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Selected for Warranty:</h4>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="text-slate-600">Product:</span>{' '}
                    <span className="font-medium text-slate-900">
                      {selectedProduct.manufactured_product?.name || selectedProduct.supply?.name}
                    </span>
                  </p>
                  <p>
                    <span className="text-slate-600">Serial:</span>{' '}
                    <span className="font-mono text-slate-900">{selectedProduct.serial_number}</span>
                  </p>
                  {selectedSupply ? (
                    <p>
                      <span className="text-slate-600">Component:</span>{' '}
                      <span className="font-medium text-slate-900">{selectedSupply.supply_name}</span>
                      {selectedSupply.lot_number && (
                        <span className="font-mono text-slate-600 ml-1">({selectedSupply.lot_number})</span>
                      )}
                    </p>
                  ) : (
                    <p>
                      <span className="text-slate-600">Scope:</span>{' '}
                      <span className="font-medium text-slate-900">Entire Product</span>
                    </p>
                  )}
                </div>
              </Card>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Claim Type *
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={claimData.claim_type}
                  onChange={(e) => setClaimData({ ...claimData, claim_type: e.target.value as any })}
                  required
                >
                  <option value="repair">Repair</option>
                  <option value="replacement">Replacement</option>
                  <option value="refund">Refund</option>
                  <option value="denied">Denied</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Issue Description *
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  value={claimData.issue_description}
                  onChange={(e) => setClaimData({ ...claimData, issue_description: e.target.value })}
                  placeholder="Describe the issue in detail..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Handled By
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={claimData.handled_by}
                    onChange={(e) => setClaimData({ ...claimData, handled_by: e.target.value })}
                    placeholder="Person handling the claim"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Estimated Cost
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={claimData.cost}
                    onChange={(e) => setClaimData({ ...claimData, cost: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? 'Creating Claim...' : 'Create Warranty Claim'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
